import { useQueries, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, markRaw, toValue, type MaybeRefOrGetter } from 'vue';
import { requireFileUrl, useFileUrl } from './useFileUrl';
import { fileKeys } from './file.keys';

export type TFileImageSource = number | string | null | undefined;

/** DOM images, avatars and canvas images share loading and one retry with a fresh signed URL. */
export function useFileImage(source: MaybeRefOrGetter<TFileImageSource>, enabled: MaybeRefOrGetter<boolean> = true) {
  const options = useImageQueryOptions();
  const query = useQuery(
    computed(() => ({ ...options(toValue(source)), enabled: toValue(enabled) && !!toValue(source) })),
  );
  const url = computed(() => (query.isError.value ? undefined : query.data.value?.src));
  return { ...query, url };
}

export function useFileImages(sources: MaybeRefOrGetter<TFileImageSource[]>) {
  const options = useImageQueryOptions();
  const queries = useQueries({ queries: computed(() => toValue(sources).map(options)) });
  return computed(() => queries.value.map((query) => (query.isError ? undefined : (query.data ?? undefined))));
}

function useImageQueryOptions() {
  const client = useQueryClient();
  const { resolve, scope, scanRetryInterval } = useFileUrl();
  const resolveForImage = async (...args: Parameters<typeof resolve>): Promise<string | null> => {
    try {
      const download = await resolve(...args);
      if (!download.url && download.malwareScanStatus === 'pending') return null;
      return requireFileUrl(download);
    } catch (error) {
      // Keep FileImage mounted while a pending scan is retried after a transient request failure.
      if (scanRetryInterval(args[0])) return null;
      throw error;
    }
  };
  return (source: TFileImageSource) => {
    const queryKey = fileKeys.image(scope.value, source);
    let hasLoadedImage = false;
    return {
      queryKey,
      enabled: !!source,
      queryFn: async () => {
        // New views load a usable URL; subsequent checks share the latest decoded pixels for this file/scope.
        const cached = client.getQueryState<HTMLImageElement | null>(queryKey);
        // An error may have removed the DOM image, so recovery must load a fresh, usable URL.
        const image = await loadFileImage(
          source,
          resolveForImage,
          hasLoadedImage && cached?.status === 'success' ? (cached.data ?? undefined) : undefined,
        );
        hasLoadedImage = !!image;
        return image;
      },
      // Revalidate on display, but leave already loaded pixels alone while the view stays open.
      staleTime: 0,
      refetchOnMount: 'always' as const,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      // Poll only while this authorized file is awaiting a scan, including the uploader's private preview.
      refetchInterval: () => (typeof source === 'number' ? scanRetryInterval(source) : false),
    };
  };
}

async function loadFileImage(
  source: TFileImageSource,
  resolve: (fileId: number, force?: boolean) => Promise<string | null>,
  cached?: HTMLImageElement,
) {
  if (!source) return undefined;
  if (typeof source === 'string') return loadImage(source);

  const url = await resolve(source);
  if (!url) return null;
  // A File ID pins immutable bytes. Reuse them only after the resolver confirms access and scan status.
  if (cached) return cached;
  try {
    return await loadImage(url);
  } catch {
    const renewedUrl = await resolve(source, true);
    return renewedUrl ? loadImage(renewedUrl) : null;
  }
}

async function loadImage(url: string): Promise<HTMLImageElement> {
  const image = new Image();
  image.src = url;
  await image.decode();
  return markRaw(image);
}
